import './WidgetStyle.css'; 
import './ResponsiveFonts.css';
import './MobileBreakpoints.css';
import { useFont } from '../../Design/FontContext';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import DeleteModal from './DeleteModal';
import { getTextEffectStyles } from '../utils/textEffectsUtils';
import '../WidgetEditComponents/TextEffectsAnimations.css';
import { generateLayoutStyles, getResponsiveValue, appendPx } from '../WidgetEditComponents/LayoutUtils';
import HighlightText from '../WidgetEditComponents/HighlightText';
import AIRefreshIcon from '../../components/AIRefreshIcon';

// appendPx wird jetzt aus LayoutUtils importiert

function AchievementsBanner({ data, onRemove, isSelected, isEditMode, onUpdate, onSelectWidget }) {
    const deleteLogo = '/img/delete.svg';
    const fonts = useFont();
    
    // INLINE-EDITING STATES
    const [isInlineEditing, setIsInlineEditing] = useState(null);
    const [inlineValue, setInlineValue] = useState('');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    
    // Animation Trigger State (2025 Animation System)
    const [animationKey, setAnimationKey] = useState(0);
    
    const widgetRef = useRef(null);

    // Animation Trigger Effect (2025 Animation System)
    useEffect(() => {
        if (data.triggerAnimation && isEditMode) {
            setAnimationKey(prev => prev + 1);
        }
    }, [data.triggerAnimation, isEditMode]);

    // ✅ RESPONSIVE LAYOUT DETECTION - NEW LAYOUT SYSTEM 2025
    const [isMobileLayout, setIsMobileLayout] = useState(false);
    const containerRef = useRef(null);
    
    // ✅ MOBILE BREAKPOINT DETECTION (750px Standard)
    useEffect(() => {
        const checkContainerWidth = () => {
            if (containerRef.current) {
                const containerWidth = containerRef.current.offsetWidth;
                setIsMobileLayout(containerWidth < 750);
            }
        };

        checkContainerWidth();
        window.addEventListener('resize', checkContainerWidth);
        
        const observer = new ResizeObserver(checkContainerWidth);
        if (containerRef.current) {
            observer.observe(containerRef.current);
        }

        return () => {
            window.removeEventListener('resize', checkContainerWidth);
            observer.disconnect();
        };
    }, []);

    // ✅ BACKWARDS COMPATIBILITY: Keep old variables for existing code
    const isMobile = isMobileLayout;
    const isBig = !isMobileLayout; // Simplified for achievements banner

    // FONT LOGIC
    const getFont = (fontField, fallback = 'Arial, sans-serif') => {
        if (data[fontField] && data[fontField].trim() !== '') {
            return data[fontField];
        }
        
        if (fontField.includes('title') || fontField.includes('number')) {
            return fonts.globalBigFont || fallback;
        } else {
            return fonts.globalSmallFont || fallback;
        }
    };

    // ✅ RESPONSIVE SIZE LOGIC - NEW LAYOUT SYSTEM 2025
    const getResponsiveSize = (baseField, fallback = 16) => {
        // Mobile: Use mobile-specific value if available
        if (isMobileLayout && data[`${baseField}Mobile`] !== undefined) {
            return data[`${baseField}Mobile`];
        }
        // Desktop: Use base value or fallback
        return data[baseField] !== undefined ? data[baseField] : fallback;
    };

    // INLINE EDITING FUNCTIONS
    const startInlineEdit = useCallback((field, currentValue) => {
        if (!isEditMode) return;
        setIsInlineEditing(field);
        setInlineValue(currentValue);
    }, [isEditMode]);

    const saveInlineEdit = useCallback(() => {
        if (isInlineEditing && onUpdate) {
            onUpdate({ [isInlineEditing]: inlineValue });
        }
        setIsInlineEditing(null);
        setInlineValue('');
    }, [isInlineEditing, inlineValue, onUpdate]);

    const cancelInlineEdit = useCallback(() => {
        setIsInlineEditing(null);
        setInlineValue('');
    }, []);

    // KEY HANDLERS
    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            saveInlineEdit();
        } else if (e.key === 'Escape') {
            e.preventDefault();
            cancelInlineEdit();
        }
    }, [saveInlineEdit, cancelInlineEdit]);

    // SMART OUTSIDE-CLICK HANDLER
    useEffect(() => {
        if (!isInlineEditing) return;

        const handleOutsideClick = (event) => {
            console.log('🔍 AchievementsBanner Outside Click Debug:', {
                target: event.target.tagName,
                isInlineEditing,
                inlineValue: inlineValue.substring(0, 20) + '...'
            });

            const isEditingInput = event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA';
            const isActionButton = event.target.hasAttribute('data-action-button') || event.target.closest('[data-action-button]');
            const isDeleteButton = event.target.closest('.delete_button');
            const isWidgetEdit = event.target.closest('.widget-edit-container');
            const isWithinWidget = widgetRef.current?.contains(event.target);
            
            // Klick auf anderes Text-Element = Outside-Click
            const isClickOnOtherTextElement = event.target.tagName === 'P' || 
                                            event.target.tagName === 'H1' || 
                                            event.target.tagName === 'H2' || 
                                            event.target.tagName === 'H3' || 
                                            event.target.tagName === 'DIV' ||
                                            event.target.tagName === 'BUTTON';
            
            const isRealOutsideClick = (!isEditingInput && !isActionButton && !isDeleteButton && !isWidgetEdit && !isWithinWidget) ||
                                      (isClickOnOtherTextElement && isWithinWidget && !isEditingInput);

            console.log('🔍 AchievementsBanner Outside Click Checks:', {
                isEditingInput,
                isActionButton,
                isDeleteButton,
                isWidgetEdit,
                isWithinWidget,
                isClickOnOtherTextElement,
                isRealOutsideClick
            });

            if (isRealOutsideClick && isInlineEditing) {
                saveInlineEdit();
            }
        };

        document.addEventListener('mousedown', handleOutsideClick, true);
        return () => document.removeEventListener('mousedown', handleOutsideClick, true);
    }, [isInlineEditing, inlineValue, saveInlineEdit]);

    // BORDER STYLE
    function getBorderStyle() {
        if (isEditMode) {
            return isSelected ? '2px dashed #000' : '2px dashed transparent';
        }
        return data.border ? `1.5px solid ${data.borderColor}` : 'none';
    }

    // DELETE HANDLER
    const handleDelete = (event) => {
        onRemove(event, data.id);
        setShowDeleteModal(false);
    };

    // BACKGROUND IMAGE STYLES
    const getBackgroundImageStyles = () => {
        if (!data.backgroundImage) return {};
        
        let filter = '';
        switch (data.imageEffect) {
            case 'blur':
                filter = 'blur(2px)';
                break;
            case 'grayscale':
                filter = 'grayscale(100%)';
                break;
            case 'top-fade':
                filter = 'linear-gradient(to bottom, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0.2) 100%)';
                break;
            default:
                filter = '';
        }
        
        return {
            backgroundImage: `url(${data.backgroundImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            filter: filter,
            opacity: data.backgroundImageOpacity || 0.1
        };
    };

    // FOREGROUND IMAGE STYLES
    const getForegroundImageStyles = () => {
        if (!data.showForegroundImage || !data.foregroundImage) return { display: 'none' };
        
        const position = data.foregroundImagePosition || 'left-bottom';
        let positionStyles = {};
        
        switch (position) {
            case 'left-bottom':
                positionStyles = { left: 0, bottom: 0 };
                break;
            case 'left-top':
                positionStyles = { left: 0, top: 0 };
                break;
            case 'right-bottom':
                positionStyles = { right: 0, bottom: 0 };
                break;
            case 'right-top':
                positionStyles = { right: 0, top: 0 };
                break;
            default:
                positionStyles = { left: 0, bottom: 0 };
        }
        
        return {
            position: 'absolute',
            width: appendPx(data.foregroundImageWidth || 300),
            height: appendPx(data.foregroundImageHeight || 400),
            borderRadius: appendPx(data.foregroundImageBorderRadius || 12),
            backgroundImage: `url(${data.foregroundImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            zIndex: 10,
            ...positionStyles
        };
    };

    // 🚀 RESPONSIVE LAYOUT STYLES - Mit Revolutionary Layout System
    const layoutStyles = generateLayoutStyles(data, isMobile, {
        backgroundColor: data.backgroundColor,
        borderRadius: appendPx(data.borderRadius),
        border: getBorderStyle(),
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%'
    });

    // WIDGET STYLES mit Revolutionary Layout System
    const widgetStyle = {
        ...layoutStyles,
        // Fallback für alte Properties (Backwards Compatibility)
        ...(layoutStyles.height === 'auto' && !layoutStyles.minHeight && {
            minHeight: appendPx(data.height || 400)
        }),
        // ✅ RESPONSIVE PADDING - NEW LAYOUT SYSTEM 2025
        padding: `${getResponsiveValue(data, 'paddingTop', isMobileLayout, isMobileLayout ? 60 : 80)}px ${isMobileLayout ? 20 : 40}px ${getResponsiveValue(data, 'paddingBottom', isMobileLayout, isMobileLayout ? 60 : 80)}px ${isMobileLayout ? 20 : 40}px`
    };

    // ✅ TITLE STYLES - Responsive Font System
    const titleStyles = {
        ...getTextEffectStyles(data.titleEffect, data.titleColor),
        color: data.titleColor,
        fontSize: appendPx(getResponsiveSize('titleSize', 32)),
        fontWeight: data.titleWeight,
        fontFamily: getFont('titleFont'),
        textAlign: 'center',
        marginBottom: isMobileLayout ? 
            appendPx(data.titleMarginMobile || 30) : 
            '60px',
        zIndex: 20,
        position: 'relative',
        // ✅ CSS Custom Properties für Responsive Fonts
        '--mobile-size': appendPx(data.titleSizeMobile || data.titleSize || 32),
        '--normal-size': appendPx(data.titleSize || 32),
        '--big-size': appendPx(data.titleSizeBig || data.titleSize || 32)
    };

    // LAYOUT CONFIGURATION - Simplified to classic-horizontal only
    const getLayoutConfig = () => {
        return {
            containerDirection: 'row',
            containerJustify: 'center',
            containerAlign: 'center',
            containerWrap: 'nowrap',
            itemAlign: 'center',
            itemTextAlign: 'center',
            description: 'Classic horizontal layout with centered achievements'
        };
    };

    const layoutConfig = getLayoutConfig();

    // ✅ ACHIEVEMENTS CONTAINER STYLES - Responsive Layout Switch
    const achievementsContainerStyle = {
        display: 'flex',
        // ✅ DESKTOP: horizontal (row) → MOBILE: vertical (column)
        flexDirection: isMobileLayout ? 'column' : 'row',
        alignItems: 'center',
        justifyContent: 'center',
        flexWrap: 'nowrap',
        // ✅ RESPONSIVE GAP: Tighter spacing on mobile
        gap: isMobileLayout ? 
            appendPx(data.containerSpacingMobile || 30) : 
            appendPx(data.containerSpacing || 80),
        zIndex: 20,
        position: 'relative',
        width: '100%',
        maxWidth: '1200px'
    };

    // CONTAINER STYLE SYSTEM - Simplified to classic-with-line only
    const getContainerStyleConfig = () => {
        return {
            showLine: true,
            showIcon: true,
            containerBackground: 'transparent',
            containerBorder: 'none',
            containerShadow: 'none',
            containerPadding: '0px',
            containerRadius: '0px',
            animation: 'none',
            description: 'Classic style with line and icon'
        };
    };

    const containerStyleConfig = getContainerStyleConfig();

    // ✅ INDIVIDUAL ACHIEVEMENT STYLES - Mit Frame-Support und Mobile-Optimierung
    const getAchievementStyle = () => {
        const baseStyle = {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            minWidth: '200px',
            // ✅ MOBILE-SPECIFIC STYLING
            ...(isMobileLayout && {
                maxWidth: appendPx(data.mobileMaxWidth || 280),
                width: '100%',
                margin: '0 auto'
            })
        };
        
        // ✅ FRAME MODE: Elegante Outline-Boxen mit einstellbaren Properties
        if (data.showFrames) {
            const shadowIntensity = data.frameShadowIntensity !== undefined ? data.frameShadowIntensity : 6;
            const blurIntensity = data.frameBlurIntensity !== undefined ? data.frameBlurIntensity : 10;
            const borderWidth = data.frameBorderWidth !== undefined ? data.frameBorderWidth : 1;
            const borderRadius = data.frameBorderRadius !== undefined ? data.frameBorderRadius : 12;
            
            // ✅ RESPONSIVE FRAME PADDING
            const padding = isMobileLayout ? 
                (data.mobileFramePadding !== undefined ? data.mobileFramePadding : 20) :
                (data.framePadding !== undefined ? data.framePadding : 24);
            
            return {
                ...baseStyle,
                background: data.frameBackgroundColor || 'rgba(255, 255, 255, 0.05)',
                border: borderWidth > 0 ? `${borderWidth}px solid ${data.frameBorderColor || '#e5e7eb'}` : 'none',
                borderRadius: appendPx(borderRadius),
                padding: appendPx(padding),
                boxShadow: shadowIntensity > 0 ? `0 2px ${shadowIntensity}px rgba(0, 0, 0, 0.06)` : 'none',
                transition: 'all 0.3s ease',
                backdropFilter: blurIntensity > 0 ? `blur(${blurIntensity}px)` : 'none'
            };
        }
        
        // ✅ CLASSIC MODE: Transparente Container
        return {
            ...baseStyle,
            background: containerStyleConfig.containerBackground,
            border: containerStyleConfig.containerBorder,
            boxShadow: containerStyleConfig.containerShadow,
            padding: containerStyleConfig.containerPadding,
            borderRadius: containerStyleConfig.containerRadius,
            backdropFilter: containerStyleConfig.backdropFilter || 'none'
        };
    };

    // ✅ RESPONSIVE NUMBER STYLES
    const getNumberStyle = () => ({
        color: data.numberColor,
        fontSize: appendPx(getResponsiveSize('numberSize', 48)),
        fontWeight: data.numberWeight,
        fontFamily: getFont('numberFont'),
        marginBottom: '10px',
        lineHeight: 1.1,
        // ✅ CSS Custom Properties für Responsive Fonts
        '--mobile-size': appendPx(data.numberSizeMobile || data.numberSize || 48),
        '--normal-size': appendPx(data.numberSize || 48),
        '--big-size': appendPx(data.numberSizeBig || data.numberSize || 48)
    });

    // ✅ RESPONSIVE TEXT STYLES
    const getTextStyle = () => ({
        color: data.textColor,
        fontSize: appendPx(getResponsiveSize('textSize', 16)),
        fontWeight: data.textWeight,
        fontFamily: getFont('textFont'),
        marginBottom: '0px',
        lineHeight: 1.4,
        // ✅ CSS Custom Properties für Responsive Fonts
        '--mobile-size': appendPx(data.textSizeMobile || data.textSize || 16),
        '--normal-size': appendPx(data.textSize || 16),
        '--big-size': appendPx(data.textSizeBig || data.textSize || 16)
    });

    // ✅ SEPARATOR STYLES - CSS-gestylte Linien-Objekte
    const getSeparatorStyle = () => {
        const separatorType = data.separatorStyle || 'line';
        const color = data.lineColor || data.textColor || '#6b7280';
        const height = data.lineHeight || 60;
        const width = data.lineWidth || 2;
        
        const baseContainerStyle = {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '20px',
            height: appendPx(height),
            width: '100%'
        };
        
        switch (separatorType) {
            case 'line':
                return {
                    ...baseContainerStyle,
                    '&::before': {
                        content: '""',
                        width: '60px',
                        height: appendPx(width),
                        backgroundColor: color
                    }
                };
            
            case 'gradient-line':
                return {
                    ...baseContainerStyle,
                    background: `linear-gradient(90deg, transparent 0%, ${color} 50%, transparent 100%)`,
                    height: appendPx(width)
                };
            
            case 'dotted-line':
                return {
                    ...baseContainerStyle,
                    borderTop: `${width}px dotted ${color}`,
                    width: '80px'
                };
            
            case 'dashed-line':
                return {
                    ...baseContainerStyle,
                    borderTop: `${width}px dashed ${color}`,
                    width: '80px'
                };
            
            case 'double-line':
                return {
                    ...baseContainerStyle,
                    borderTop: `${width}px solid ${color}`,
                    borderBottom: `${width}px solid ${color}`,
                    height: appendPx(Math.max(width * 3, 6)),
                    width: '60px'
                };
            
            case 'none':
                return { display: 'none' };
            
            default:
                return baseContainerStyle;
        }
    };

    // ✅ SEPARATOR RENDER FUNCTION - Verschiedene CSS-Objekte
    const renderSeparator = () => {
        const separatorType = data.separatorStyle || 'line';
        const color = data.lineColor || data.textColor || '#6b7280';
        const height = data.lineHeight || 60;
        const width = data.lineWidth || 2;
        const spacing = data.separatorSpacing !== undefined ? data.separatorSpacing : 20;
        
        if (separatorType === 'none') return null;
        
        const containerStyle = {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginTop: appendPx(spacing),
            marginBottom: appendPx(spacing),
            height: appendPx(height),
            width: '100%'
        };
        
        switch (separatorType) {
            case 'line':
                return (
                    <div style={containerStyle}>
                        <div style={{
                            width: appendPx(width),
                            height: appendPx(height),
                            backgroundColor: color
                        }} />
                    </div>
                );
            
            case 'gradient-line':
                return (
                    <div style={containerStyle}>
                        <div style={{
                            width: appendPx(width),
                            height: appendPx(height),
                            background: `linear-gradient(180deg, transparent 0%, ${color} 50%, transparent 100%)`
                        }} />
                    </div>
                );
            
            case 'dotted-line':
                return (
                    <div style={containerStyle}>
                        <div style={{
                            width: '0',
                            height: appendPx(height),
                            borderLeft: `${width}px dotted ${color}`
                        }} />
                    </div>
                );
            
            case 'dashed-line':
                return (
                    <div style={containerStyle}>
                        <div style={{
                            width: '0',
                            height: appendPx(height),
                            borderLeft: `${width}px dashed ${color}`
                        }} />
                    </div>
                );
            
            case 'double-line':
                return (
                    <div style={containerStyle}>
                        <div style={{
                            width: appendPx(Math.max(width * 3, 6)),
                            height: appendPx(height),
                            borderLeft: `${width}px solid ${color}`,
                            borderRight: `${width}px solid ${color}`,
                            position: 'relative'
                        }} />
                    </div>
                );
            
            case 'wave-line':
                return (
                    <div style={containerStyle}>
                        <svg width="20" height={height} viewBox="0 0 20 80" style={{ overflow: 'visible' }}>
                            <path
                                d="M10,0 Q0,20 10,40 T10,80"
                                stroke={color}
                                strokeWidth={width}
                                fill="none"
                                strokeLinecap="round"
                            />
                        </svg>
                    </div>
                );
            
            case 'zigzag-line':
                return (
                    <div style={containerStyle}>
                        <svg width="20" height={height} viewBox="0 0 20 80" style={{ overflow: 'visible' }}>
                            <path
                                d="M5,0 L15,10 L5,20 L15,30 L5,40 L15,50 L5,60 L15,70 L5,80"
                                stroke={color}
                                strokeWidth={width}
                                fill="none"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </div>
                );
            
            case 'diamond-pattern':
                return (
                    <div style={containerStyle}>
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '8px'
                        }}>
                            {[...Array(3)].map((_, i) => (
                                <div
                                    key={i}
                                    style={{
                                        width: appendPx(Math.max(width * 2, 6)),
                                        height: appendPx(Math.max(width * 2, 6)),
                                        backgroundColor: color,
                                        transform: 'rotate(45deg)'
                                    }}
                                />
                            ))}
                        </div>
                    </div>
                );
            
            case 'circle-dots':
                return (
                    <div style={containerStyle}>
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '6px'
                        }}>
                            {[...Array(5)].map((_, i) => (
                                <div
                                    key={i}
                                    style={{
                                        width: appendPx(Math.max(width * 1.5, 4)),
                                        height: appendPx(Math.max(width * 1.5, 4)),
                                        backgroundColor: color,
                                        borderRadius: '50%'
                                    }}
                                />
                            ))}
                        </div>
                    </div>
                );
            
            case 'spiritual-ornament':
                return (
                    <div style={containerStyle}>
                        <svg width="30" height={height} viewBox="0 0 30 100" style={{ overflow: 'visible' }}>
                            <g stroke={color} strokeWidth={width} fill="none">
                                {/* Zentrale Linie */}
                                <line x1="15" y1="20" x2="15" y2="80" />
                                {/* Ornamente */}
                                <circle cx="15" cy="30" r="3" />
                                <circle cx="15" cy="50" r="4" />
                                <circle cx="15" cy="70" r="3" />
                                {/* Kleine Akzente */}
                                <line x1="10" y1="45" x2="20" y2="55" />
                                <line x1="10" y1="55" x2="20" y2="45" />
                            </g>
                        </svg>
                    </div>
                );
            
            default:
                return (
                    <div style={containerStyle}>
                        <div style={{
                            width: appendPx(width),
                            height: appendPx(height),
                            backgroundColor: color
                        }} />
                    </div>
                );
        }
    };

    // ✅ RESPONSIVE ICON STYLES
    const getIconStyle = () => {
        // Mobile-specific icon size
        const iconSize = isMobileLayout ? 
            (data.mobileIconSize !== undefined ? data.mobileIconSize : 32) :
            (data.iconSize !== undefined ? data.iconSize : 40);
            
        return {
            width: appendPx(iconSize),
            height: appendPx(iconSize),
            filter: `hue-rotate(${data.iconColor ? '0deg' : '0deg'})`, // Color adjustment if needed
        };
    };

    // RENDER ACHIEVEMENT ITEM
    const renderAchievement = (number, text, icon, index) => {
        const isFramedLayout = data.showFrames;
        
        return (
            <div 
                key={index} 
                className="achievement-item"
                style={getAchievementStyle()}
            >
                {/* ✅ FRAMED CARDS: Icon oben, Classic: Icon unten */}
                {isFramedLayout && data.showIcons !== false && icon && (
                    <img
                        src={icon}
                        alt={`Achievement ${index + 1} Icon`}
                        style={{
                            ...getIconStyle(),
                            marginBottom: '16px',
                            order: 1
                        }}
                    />
                )}

                {/* Number */}
                {isInlineEditing === `achievement${index + 1}Number` ? (
                    <input
                        type="text"
                        value={inlineValue}
                        onChange={(e) => setInlineValue(e.target.value)}
                        onBlur={saveInlineEdit}
                        onKeyDown={handleKeyDown}
                        style={{
                            ...getNumberStyle(),
                            background: 'rgba(255, 255, 255, 0.1)',
                            border: 'none',
                            textAlign: 'center',
                            outline: 'none',
                            width: 'auto',
                            minWidth: '80px',
                            maxWidth: '150px',
                            borderRadius: '4px',
                            boxShadow: '0 0 0 1px rgba(0, 0, 0, 0.1)',
                            padding: '2px 8px',
                            order: 2
                        }}
                        autoFocus
                    />
                ) : (
                    <div
                        style={{
                            ...getNumberStyle(),
                            order: 2
                        }}
                        onClick={() => startInlineEdit(`achievement${index + 1}Number`, number)}
                        className={isEditMode ? 'inline-editable' : ''}
                    >
                        {number}
                    </div>
                )}

                {/* Text */}
                {isInlineEditing === `achievement${index + 1}Text` ? (
                    <input
                        type="text"
                        value={inlineValue}
                        onChange={(e) => setInlineValue(e.target.value)}
                        onBlur={saveInlineEdit}
                        onKeyDown={handleKeyDown}
                        style={{
                            ...getTextStyle(),
                            background: 'rgba(255, 255, 255, 0.1)',
                            border: 'none',
                            textAlign: 'center',
                            outline: 'none',
                            width: 'auto',
                            minWidth: '120px',
                            maxWidth: '200px',
                            borderRadius: '4px',
                            boxShadow: '0 0 0 1px rgba(0, 0, 0, 0.1)',
                            padding: '2px 8px',
                            order: 3
                        }}
                        autoFocus
                    />
                ) : (
                    <div
                        style={{
                            ...getTextStyle(),
                            order: 3
                        }}
                        onClick={() => startInlineEdit(`achievement${index + 1}Text`, text)}
                        className={isEditMode ? 'inline-editable' : ''}
                    >
                        {text}
                    </div>
                )}

                {/* Separator - CSS-gestylte Linien-Objekte */}
                {data.showSeparator !== false && (
                    <div style={{ order: 4 }}>
                        {renderSeparator()}
                    </div>
                )}

                {/* Icon - Classic Layout: unten */}
                {!isFramedLayout && data.showIcons !== false && icon && (
                    <img
                        src={icon}
                        alt={`Achievement ${index + 1} Icon`}
                        style={{
                            ...getIconStyle(),
                            order: 5
                        }}
                    />
                )}
            </div>
        );
    };

    return (
        <div 
            ref={(el) => {
                containerRef.current = el;
                widgetRef.current = el;
            }} 
            className="widget-container achievements-banner-widget" 
            style={widgetStyle} 
            onClick={onSelectWidget} 
            key={animationKey}
        >
            {/* Background Image Overlay */}
            {data.backgroundImage && (
                <div
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 1,
                        ...getBackgroundImageStyles()
                    }}
                />
            )}

            {/* Foreground Image */}
            <div style={getForegroundImageStyles()} />

            {/* Delete Button */}
            {isEditMode && (
                <button
                    className="delete_button"
                    onClick={(e) => {
                        e.stopPropagation();
                        setShowDeleteModal(true);
                    }}
                    style={{ zIndex: 100 }}
                >
                    <img className="delete_logo" src={deleteLogo} alt="Löschen" />
                </button>
            )}
            
            {/* 🤖 AI REFRESH ICON */}
            {isEditMode && (
                <AIRefreshIcon 
                    widget={{
                        ...data,
                        type: 'AchievementsBanner'
                    }}
                    spaceId={null}
                    onContentRefreshed={(refreshedWidget) => {
                        console.log('🤖 [ACHIEVEMENTSBANNER] AI-refreshed content received:', refreshedWidget);
                        onUpdate(refreshedWidget);
                    }}
                    position="top-left"
                />
            )}

            {/* Delete Modal */}
            {isEditMode && showDeleteModal && (
                <DeleteModal
                    isOpen={showDeleteModal}
                    onClose={() => setShowDeleteModal(false)}
                    onConfirm={handleDelete}
                />
            )}

            {/* Main Title - with Show/Hide Toggle und HighlightText Support */}
            {data.showTitleText !== false && (
                isInlineEditing === 'titleText' ? (
                    <input
                        type="text"
                        value={inlineValue}
                        onChange={(e) => setInlineValue(e.target.value)}
                        onBlur={saveInlineEdit}
                        onKeyDown={handleKeyDown}
                        style={{
                            ...titleStyles,
                            background: 'rgba(255, 255, 255, 0.1)',
                            border: 'none',
                            textAlign: 'center',
                            outline: 'none',
                            width: 'auto',
                            minWidth: '200px',
                            maxWidth: '600px',
                            borderRadius: '4px',
                            boxShadow: '0 0 0 1px rgba(0, 0, 0, 0.1)',
                            padding: '2px 8px'
                        }}
                        autoFocus
                    />
                ) : (
                    // ✅ CLEAN CONDITION: Inline-Editing nur wenn KEINE Highlights aktiv
                    (!data.titleHighlightWords || data.titleHighlightWords.trim() === '') ? (
                        // 🔧 NORMALE INLINE-EDITING (ohne Highlights)
                        <h2
                            style={titleStyles}
                            onClick={() => startInlineEdit('titleText', data.titleText)}
                            className={isEditMode ? 'inline-editable' : ''}
                        >
                            {data.titleText}
                        </h2>
                    ) : (
                        // 🎨 HIGHLIGHT-MODUS (nur Anzeige, kein Inline-Editing)
                        <h2 style={{
                            ...titleStyles,
                            whiteSpace: 'pre-wrap'
                        }}>
                            <HighlightText
                                text={data.titleText}
                                highlightWords={data.titleHighlightWords}
                                baseStyle={{}}
                                highlightFont={data.titleHighlightFont || fonts.globalSmallFont}
                                highlightColor={data.titleHighlightColor}
                                highlightWeight={data.titleHighlightWeight}
                                highlightSize={data.titleHighlightSize || data.titleSize}
                                highlightSizeMobile={data.titleHighlightSizeMobile || data.titleSizeMobile}
                                highlightSizeBig={data.titleHighlightSizeBig || data.titleSizeBig}
                                highlightEffect={data.titleHighlightEffect}
                                highlightMarginLeft={data.titleHighlightMarginLeft}
                                highlightMarginRight={data.titleHighlightMarginRight}
                                getTextEffectStyles={getTextEffectStyles}
                            />
                        </h2>
                    )
                )
            )}

            {/* Achievements Container */}
            <div className="achievements-container" style={achievementsContainerStyle}>
                {renderAchievement(data.achievement1Number, data.achievement1Text, data.achievement1Icon, 0)}
                {renderAchievement(data.achievement2Number, data.achievement2Text, data.achievement2Icon, 1)}
                {renderAchievement(data.achievement3Number, data.achievement3Text, data.achievement3Icon, 2)}
            </div>
        </div>
    );
}

export default AchievementsBanner;
